import { getAggregateError, } from "@bracket/catch";
import { API_URLS } from "@constants";
import * as G from "@globals";
import { awaitTruthy, } from "@store/helpers";
import * as Q from "./queries";
import type { PricingGQLResponse, } from "./types";


/**
 * Makes a GraphQL query to the configured API using wallet context if available.
 *
 * @param {keyof typeof Q} queryName - The name of the query function from Q.
 * @param {(string | number)[]} args - Arguments to pass to the query-generating function.
 * @returns {Promise<any>} The data returned from the GraphQL API.
 * @throws {Error} If the response is not ok or invalid.
 */
export const gql = async <Q extends keyof typeof Q>( queryName: Q, ...args: PricingGQLResponse<Q>["args"] ): Promise<PricingGQLResponse<Q>["result"]> => {
	const { useStore, } = await import("@store/useStore");
	await awaitTruthy(useStore, state => state.wallet.address);
	const { address, } = G.wallet.getBracketWallet();
	const { GRAPHQL_API, } = API_URLS;
	const query = Q[queryName](...args);
	const headers: HeadersInit = {
		"Content-Type": "application/json",
		...(address ? { wallet: address, } : {}),
	};
	const response = await fetch(GRAPHQL_API, {
		method: "POST",
		body: JSON.stringify({ query, }),
		headers,
	});
	if(response.ok) {
		const { data, errors, } = await response.json();
		if(!errors) {
			return data;
		}
		throw getAggregateError(new Error(`pricing::helpers GQL failed`), errors[0].message);
	}
	throw getAggregateError(new Error(`pricing::helpers GQL failed`), "graphql query has failed.");
};
